import { gql, ApolloProvider } from "@apollo/client";
import { PropsWithChildren, useEffect, useMemo, createContext, useState, useContext } from "react";
import {
  createMarlinGraphqlClient
} from "~/services/graphqlClient";

import {
    GetAvailableOrgsQuery,
  useGetAvailableOrgsQuery,
} from "~/graphql/__generated__/graphql";
import { useAuth } from "~/state/auth";
import NoCommandCenterPermission from "~/components/login/NoCommandCenterPermission";

type AvailableOrgs = GetAvailableOrgsQuery["currentUser"]["organizations"]

type OrganizationContextType = {
  organizationId?: number;
  organizationName?: string;
  setOrganizationId?: (orgId?: number) => void;
  overrideProviderId?: (providerId: number) => void;
  availableOrganizations?: AvailableOrgs;
  setAvailableOrganizations?: (availableOrgs: AvailableOrgs) => void;
};

const OrganizationContext = createContext({ availableOrganizations: [] } as OrganizationContextType);
export const useOrganization = (): OrganizationContextType => useContext(OrganizationContext);
export const useCurrentOrg = (): AvailableOrgs[number] | undefined => {
  const { availableOrganizations, organizationId } = useOrganization()
  return availableOrganizations?.find(org => org.id === organizationId)
}

export const GraphqlProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [organizationId, setOrgId] = useState(parseInt(localStorage?.organizationId) || undefined);
  const client = useMemo(() => createMarlinGraphqlClient(organizationId), [organizationId]);
  const [availableOrganizations, setAvailableOrganizations] = useState<AvailableOrgs | undefined>(undefined);
  const organizationName = useMemo(() => {
    const org = availableOrganizations?.find(o => o.id==organizationId);
    return org?.name || localStorage?.organizationName;
  }, [organizationId, availableOrganizations])

  useEffect(() => {if(localStorage) { localStorage.organizationName = organizationName; }}, [organizationName]);

  const setOrganizationId = (newId?: number) => {
    if (localStorage) {
      localStorage.organizationId = newId;
    }
    setOrgId(newId);
  }

  const overrideProviderId = (providerId: number) => {
    if (availableOrganizations && organizationId) {
      setAvailableOrganizations(availableOrganizations.map(o => o.id === organizationId ? { ...o, providerId }: o))
    }
  }

  return <OrganizationContext.Provider value={{ organizationId, organizationName, setOrganizationId, availableOrganizations, setAvailableOrganizations, overrideProviderId }}>
    <ApolloProvider client={client}>
      <OrgProvider>{children}</OrgProvider>
    </ApolloProvider>
  </OrganizationContext.Provider>
};

const OrgProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, inactivity } = useAuth();
  const {organizationId, availableOrganizations, setOrganizationId, setAvailableOrganizations} = useOrganization();

  const { data, error } = useGetAvailableOrgsQuery({
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (error) {
      console.error(error);
    }

    if (!organizationId && data) {
      setOrganizationId?.(data.currentUser.organizations[0].id);
    }

    if (data) {
      setAvailableOrganizations?.(data.currentUser.organizations);
    }
  }, [data, error]);

  useEffect(() => {
    if(inactivity) {
      if(organizationId && availableOrganizations) {
        const idleTimeout = availableOrganizations?.find(o => o.id==organizationId)?.settings?.idleSessionTimeoutMinutes;
        inactivity.setTimeoutMs(idleTimeout ? idleTimeout * 60 * 1000 : undefined);  
      }
      else {
        inactivity.setTimeoutMs(undefined);
      }
    }
  }, [inactivity, organizationId, availableOrganizations]);

  return error? <NoCommandCenterPermission /> : children;
}

gql(`
  query GetAvailableOrgs @permission(requires: isAuthenticated) {
    currentUser {
      id
      userId
      organizations {
          id
          name
          organizationUserId
          roles
          perms
          serviceLineIds
          providerId
          settings {
            enableMobileNotifications
            mobileNotificationsWindow
            enableLocationBasedStatusUpdates
            idleSessionTimeoutMinutes
            mobileIdleLockTimeoutMinutes
            mobileIdleSignOutMinutes
            mobileUnlockMethod
            shufflerLeadTime
            barcodeTypes
          }
      }
    }
  }
`);
